import React, { useContext, useState, useRef, useEffect, useMemo } from 'react';
import ToolButton, { ToolButtonType } from '../../../tools/tool-button';
import { t } from 'i18next';
import '../../../App.css';
import './new-session-panel.css';
import { apiPrefix } from '../../../utils/functions';
import { ServiceDetails, ComputeServiceType, Site, UserType } from '../../../utils/types';
import { computeSessionDurationOptions } from '../../../utils/common/constants';
import { useAppSelector, useAppDispatch } from '../../../app/hooks';
import { store } from '../../../app/store';

// classes
import ToolSmall from '../../../tools/tool-small';
import PleaseWait from '../../../tools/please-wait';

// import types.
import { CanfarServiceType } from '../../../utils/types';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import {
  displaySession,
  launchCanfarSession,
  refreshSRCNetServices,
} from '../../interactive-sessions/interactive-sessions-actions';
import { autoStageForCatalog } from '../../../app-actions';
import {
  addSessions,
  closeTab,
  removeSessions,
} from '../../interactive-sessions/interactive-sessions-slice';
import { useTranslation } from 'react-i18next';

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

const NewSessionPanel: React.FC<{
  onClose: () => void;
  site_uuid?: string;
  service_uuid?: string;
  service_type?: string;
  tab?: string;
  stagingCatalog?: string;
}> = (props: {
  onClose: () => void;
  site_uuid?: string;
  service_uuid?: string;
  service_type?: string;
  tab?: string;
  stagingCatalog?: string;
}) => {
  const { t } = useTranslation();
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // global state variables.
  const sG_LoggedInRefreshTrigger: number | undefined = useAppSelector(
    (state) => state.global.loggedInRefreshTrigger,
  );
  const sG_SiteCapabilitiesTokenObtained: boolean | undefined = useAppSelector(
    (state) => state.global.siteCapabilitiesTokenObtained,
  );
  const sG_userType: UserType = useAppSelector(
    (state) => state.global.userType ?? UserType.STANDARD,
  );
  const sG_site: Site | undefined = useAppSelector((state) => state.global.site);

  // data state variables.
  const sD_sites: Site[] = useAppSelector((state) => state.data.sites);
  const sD_canfarServices: CanfarServiceType[] = useAppSelector(
    (state) => state.data.canfarServices || [],
  );
  const sD_computeServices: ComputeServiceType[] = useAppSelector(
    (state) => state.data.computeServices,
  );

  // hold the list of compute services from the site-capabilities catalog.
  const sSessions: ComputeServiceType[] = useAppSelector(
    (state) => state.interactiveSessions.sessions,
  );

  // Sites returned by the execution broker (when using the broker tab).
  // These are fetched from the backend once the user selects service type.
  const [sBrokerSites, setBrokerSites] = useState<Site[]>([]);

  // false = Broker step 1, true = Broker step 2
  const [isBrokerStepTwo, setIsBrokerStepTwo] = useState(false);

  // Get translated options
  const sessionDurationOptions = computeSessionDurationOptions(t);

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  const UNDEFINED_SITE: Site = {};

  // find the default node and site, if one is supplied.
  let NODE: string = '';
  let SITE_UUID: string = '';
  let SERVICE_TYPE: string = '';
  let SERVICE_UUID: string = '';
  let TAB: string = '';
  if (props.site_uuid !== undefined) {
    const site: Site | undefined = sD_sites.find(
      (site) => site.site_uuid === props.site_uuid,
    );
    if (site !== undefined) {
      NODE = site.node ?? '';
      SITE_UUID = props.site_uuid;
    }
  }

  if (props.service_uuid !== undefined) {
    const service: ComputeServiceType | undefined = sD_computeServices.find(
      (service) => service.compute_service_id === props.service_uuid,
    );
    if (service !== undefined) {
      NODE = service.site.node ?? '';
      SITE_UUID = service.site.site_uuid ?? '';
      SERVICE_TYPE = service.session_type;
      SERVICE_UUID = service.compute_service_id;
    }
  }

  if (props.service_type !== undefined) {
    SERVICE_TYPE = props.service_type;
  }

  if (props.tab !== undefined) {
    TAB = props.tab;
  }

  // True when the dialog was opened with a fixed site/service.
  // In that case we must not override node/site with the global site default.
  const siteLockedByProps =
    props.site_uuid !== undefined || props.service_uuid !== undefined;

  // select boxes.
  const [sNodeValue, setNodeValue] = useState<string>(() =>
    NODE !== '' ? NODE : (sG_site?.node ?? ''),
  );
  const [sSiteValue, setSiteValue] = useState<string>(() =>
    SITE_UUID !== '' ? SITE_UUID : (sG_site?.site_uuid ?? ''),
  );
  const [sServiceType, setServiceType] = useState<string>(SERVICE_TYPE);
  const [sDuration, setDuration] = useState<string>('');
  const [sComputeService, setComputeService] = useState<string>(SERVICE_UUID);
  const [sImage, setImage] = useState<string>('');
  const [sCores, setCores] = useState<string>('');
  const [sRam, setRam] = useState<string>('');

  // check boxes
  const [sRequireGPUs, setRequireGPUs] = useState<boolean>(false);

  // select box options.
  const [sCoreOptions, setCoreOptions] = useState<string[]>([]);
  const [sRamOptions, setRamOptions] = useState<string[]>([]);

  // input boxes.
  const [sCmd, setCmd] = useState<string>('');
  const [sArgs, setArgs] = useState<string>('');
  const [sEnv, setEnv] = useState<string>('');
  const sessionSuffix = useRef<string>(
    Math.floor(Math.random() * 1_000_000_000)
      .toString()
      .padStart(9, '0'),
  );
  const [sName, setName] = useState<string>(
    () =>
      (SERVICE_TYPE !== '' ? SERVICE_TYPE : 'notebook') +
      '-session-' +
      sessionSuffix.current,
  );

  // the canfar service of the selected site.
  const sCanfarService: CanfarServiceType | undefined = useMemo(() => {
    const canfarService: CanfarServiceType | undefined =
      sSiteValue !== ''
        ? sD_canfarServices.find((service) => service.site.site_uuid === sSiteValue)
        : undefined;

    if (process.env.NODE_ENV === 'development') {
      console.log('[NewSessionPanel] sCanfarService resolved from Redux', {
        site_uuid: sSiteValue,
        canfar_id: canfarService?.id,
        canfar_name: canfarService?.name,
        image_count: canfarService?.images?.length ?? 0,
        catalog_size: sD_canfarServices.length,
      });
    }

    return canfarService;
  }, [sD_canfarServices, sSiteValue]);

  // ignore stale resource_options responses when the site changes.
  const resourceOptionsRequestId = useRef(0);
  // Apply the global-site default once, do not re-apply when the user clears site to pick another node.
  const globalSiteDefaultAppliedRef = useRef(false);

  const [sLoadingOptions, setLoadingOptions] = useState<boolean>(false);
  const [sErrorMessage, setErrorMessage] = useState<string>('');

  // Choose the first tab shown when the dialog opens.
  const resolveInitialPage = (): string => {
    if (TAB !== '') {
      if (sG_userType === UserType.DEVELOPER || TAB === 'canfar') {
        return TAB;
      }
      return 'canfar';
    }
    return sG_userType === UserType.DEVELOPER ? 'srcnet' : 'canfar';
  };
  const [sPage, setPage] = useState<string>(resolveInitialPage);

  useEffect(() => {
    if (
      sG_userType !== UserType.DEVELOPER &&
      (sPage === 'srcnet' || sPage === 'broker')
    ) {
      setPage('canfar');
    }
  }, [sG_userType, sPage]);

  // Default node/site from the global site on the Canfar tab when none were passed in props.
  useEffect(() => {
    if (siteLockedByProps) {
      globalSiteDefaultAppliedRef.current = true;
      let node = '';
      let siteUuid = '';
      if (props.site_uuid !== undefined) {
        const site = sD_sites.find((item) => item.site_uuid === props.site_uuid);
        if (site !== undefined) {
          node = site.node ?? '';
          siteUuid = props.site_uuid;
        }
      }
      if (props.service_uuid !== undefined) {
        const service = sD_computeServices.find(
          (item) => item.compute_service_id === props.service_uuid,
        );
        if (service !== undefined) {
          node = service.site.node ?? '';
          siteUuid = service.site.site_uuid ?? '';
        }
      }
      if (siteUuid !== '') {
        setNodeValue(node);
        setSiteValue(siteUuid);
      }
      return;
    }

    if (sPage !== 'canfar') {
      return;
    }

    if (globalSiteDefaultAppliedRef.current) {
      return;
    }

    if (sG_site?.site_uuid !== undefined && sG_site.site_uuid !== '') {
      setNodeValue(sG_site.node ?? '');
      setSiteValue(sG_site.site_uuid ?? '');
      globalSiteDefaultAppliedRef.current = true;
    }
  }, [
    siteLockedByProps,
    props.site_uuid,
    props.service_uuid,
    sD_sites,
    sD_computeServices,
    sG_site?.site_uuid,
    sG_site?.node,
    sPage,
  ]);

  //  const allRawImages = useMemo(() => {
  //    return sD_canfarServices.flatMap((service) => {
  //      getSkahaResourceOptions({ canfarService: service });
  //      return service.images || [];
  //    });
  //  }, [sD_canfarServices]);
  //
  //  const sD_uniqueImages = useMemo(() => {
  //    const uniqueMap = new Map();
  //
  //    allRawImages
  //      .filter((img) => img.types.includes(sServiceType))
  //      .forEach((img) => {
  //        if (!uniqueMap.has(img.id)) {
  //          uniqueMap.set(img.id, {
  //            id: img.id,
  //            type: sServiceType,
  //          });
  //        }
  //      });
  //
  //    return Array.from(uniqueMap.values());
  //  }, [allRawImages, sServiceType]);

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Restrict input box entry to alphanumeric and hyphen only.
  //
  //	------------------------------------------------------------

  const alphanumericKeysOnly = (event: React.KeyboardEvent<HTMLInputElement>) => {
    //
    const key: number = event.charCode;
    const validChar: boolean =
      (key >= 48 && key <= 57) ||
      (key >= 65 && key <= 90) ||
      (key >= 97 && key <= 122) ||
      key === 45;
    if (validChar === false) {
      event.preventDefault();
    }
    //
  }; // alphanumericKeysOnly

  //	-------------------------------------------------
  //
  //	extract a unique list of nodes from the list
  //	of nodes and sites.
  //
  //	-------------------------------------------------

  function getNodes(args: { sites: Site[] }) {
    //
    let nodes: string[] = [];

    for (let i: number = 0; i < args.sites.length; i++) {
      //
      if (nodes.indexOf(args.sites[i].node ?? '') === -1) {
        nodes.push(args.sites[i].node ?? '');
      }
      //
    }

    // return something.
    return nodes;
    //
  } // getNodes

  //  -------------------------------------------------
  //
  //  get a unique list of service types from the
  //  compute services array.
  //
  //  -------------------------------------------------

  function getImages() {
    if (sPage === 'canfar' || isBrokerStepTwo)
      return sCanfarService?.images?.filter(
        (image) => image.types.indexOf(sServiceType) > -1 || sServiceType === '',
      );

    //    return sD_uniqueImages;
  }
  //	-------------------------------------------------
  //
  //	get a unique list of service types from the
  //	compute services array.
  //
  //	-------------------------------------------------

  function getServiceTypes(args: { node: string; site_uuid: string }) {
    //
    let service_types: string[] = [];

    if (sPage === 'srcnet') {
      //
      for (let i: number = 0; i < sD_computeServices.length; i++) {
        //
        if (
          (sD_computeServices[i].site.node === args.node || args.node === '') &&
          (sD_computeServices[i].site.site_uuid === args.site_uuid ||
            args.site_uuid === '')
        ) {
          if (
            service_types.indexOf(sD_computeServices[i].session_type) === -1 &&
            ['jupyterhub', 'carta'].indexOf(sD_computeServices[i].session_type) > -1
          ) {
            service_types.push(sD_computeServices[i].session_type);
          }
        }
        //
      }
      //
    } else {
      //
      for (let i: number = 0; i < sSessions.length; i++) {
        //
        if (
          (sSessions[i].site.node === args.node || args.node === '') &&
          (sSessions[i].site.site_uuid === args.site_uuid || args.site_uuid === '') &&
          ['canfar', 'broker'].includes(sSessions[i].resource_type ?? '') &&
          sSessions[i].compute_status === 'available'
        ) {
          if (service_types.indexOf(sSessions[i].session_type) === -1) {
            service_types.push(sSessions[i].session_type);
          }
        }
        //
      }
      //
    }

    // return something.
    return service_types;
    //
  } // getServiceTypes

  //	-------------------------------------------------
  //
  //	extract a unique list of sites from the list
  //	of nodes and sites, and a given node.
  //
  //	-------------------------------------------------

  function getSites(args: { node: string }) {
    //
    let sites: Site[] = [];

    const sourceSites = sPage === 'broker' ? sBrokerSites : sD_sites;

    for (let i: number = 0; i < sourceSites.length; i++) {
      //
      if (sourceSites[i].node === args.node) {
        if (
          sites.findIndex(
            (item) => item.site_uuid === (sourceSites[i].site_uuid ?? ''),
          ) === -1
        ) {
          sites.push(sourceSites[i]);
        }
      }
      //
    }

    // return something.
    return sites;
    //
  } // getSites

  //  -------------------------------------------------
  //
  //  function that handles a launch of a SRCNet session
  //
  //  -------------------------------------------------
  const launchSRCNetSession = async (
    computeServiceId: string,
    selectedService: ComputeServiceType,
    duration: string,
    dispatch: any,
  ) => {
    const payload = {
      compute_service_id: computeServiceId,
      notebook_path: '/user/home',
      is_embedded: selectedService.is_embedded ?? false,
      duration_days: duration ? parseInt(duration) : 2,
    };

    const tempId = Date.now();
    let newSession: ComputeServiceType = {
      ...selectedService,
      id: tempId,
      compute_status: 'pending',
      overridden_status: 'starting',
    };
    dispatch(addSessions([newSession]));
    // Open interactive-sessions immediately (same pattern as launchCanfarSessionDirect).
    displaySession({ session: newSession, infoDisplayed: true });

    try {
      const response = await fetch(`${apiPrefix()}/v1/launch_compute_session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.status === 409 || !response.ok) {
        dispatch(removeSessions({ id: [tempId], resource_type: ['srcnet'] }));
        const tabIndex: number = store
          .getState()
          .interactiveSessions.tabs.findIndex(
            (tab) => tab.compute_service_id === computeServiceId,
          );
        if (tabIndex > -1) {
          dispatch(closeTab({ tab: tabIndex }));
        }
        return {
          success: false,
          message:
            response.status === 409 ? result.detail : 'An unexpected error occurred.',
        };
      }

      // Polling logic
      let retries = 0;
      const maxRetries = 20;
      const interval = setInterval(async () => {
        let isRunning = false;
        try {
          const pollRes = await fetch(
            `${apiPrefix()}/v1/active_compute_sessions/get_session?service_id=${computeServiceId}`,
            {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
            },
          );
          if (pollRes.status === 200) {
            const service = await pollRes.json();
            isRunning = service.compute_status === 'running';
          }
        } catch (err) {
          /* silent fail for poll attempt */
        }

        if (isRunning) {
          displaySession({ session: { ...selectedService, compute_status: 'running' } });
        }

        if (isRunning || retries >= maxRetries) {
          clearInterval(interval);
          dispatch(refreshSRCNetServices());
        }
        retries++;
      }, 1000);

      return { success: true };
    } catch (err) {
      dispatch(removeSessions({ id: [tempId], resource_type: ['srcnet'] }));
      const tabIndex: number = store
        .getState()
        .interactiveSessions.tabs.findIndex(
          (tab) => tab.compute_service_id === computeServiceId,
        );
      if (tabIndex > -1) {
        dispatch(closeTab({ tab: tabIndex }));
      }
      return { success: false, message: 'Something went wrong while launching.' };
    }
  };

  //  -------------------------------------------------
  //
  //  function that handles a request to execution broker to get sites
  //
  //  -------------------------------------------------
  const getExecBrokerSite = async (
    params: {
      serviceType: string;
      rgpus: boolean;
    },
    dispatch: any,
  ) => {
    const parameters = {
      filters: {
        requires_gpu: params.rgpus,
        //      even_compute_units: 'false',
        //      site_names: '',
      },
    };
    const response = await fetch(`${apiPrefix()}/v1/global_execution/broker/find_sites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(parameters),
    });

    const result = await response.json();

    try {
      const sitesFromBroker: Site[] = Array.isArray(result)
        ? result
        : result && Array.isArray(result.sites)
          ? result.sites
          : [];

      setBrokerSites(sitesFromBroker);
    } catch (err) {
      console.warn('Could not parse broker result', err);
    }
  };

  //	-------------------------------------------------
  //
  //	function that handles a launch of a Compute Service
  //
  //	-------------------------------------------------
  const handleLaunch = async () => {
    setErrorMessage('');

    if (
      (sPage === 'srcnet' || sPage === 'broker') &&
      sG_userType !== UserType.DEVELOPER
    ) {
      return setErrorMessage('This feature is only available to developer users.');
    }

    let result;

    if (sPage === 'srcnet') {
      if (sComputeService === '')
        return setErrorMessage('Please select a service before launching');
      const selectedService = sD_computeServices.find(
        (s) => s.compute_service_id === sComputeService,
      );
      if (!selectedService) return setErrorMessage('Selected compute service is invalid');

      props.onClose();
      result = await launchSRCNetSession(
        sComputeService,
        selectedService,
        sDuration,
        dispatch,
      );
    }

    if (sPage === 'canfar' || isBrokerStepTwo) {
      if (sImage === '') return setErrorMessage('Please select an image');
      if (sName.trim() === '') return setErrorMessage('Please enter a session name');

      props.onClose();
      void launchCanfarSession({
        serviceType: sServiceType,
        sessionName: sName,
        image: sImage,
        cores: sCores,
        ram: sRam,
        cmd: sCmd,
        args: sArgs,
        env: sEnv,
        stagingCatalog: props.stagingCatalog,
        reuseExistingSession: false,
      });
      return;
    }

    if (sPage === 'broker') {
      if (sServiceType === '') return setErrorMessage('Please select a service type');
      result = await getExecBrokerSite(
        {
          serviceType: sServiceType,
          rgpus: sRequireGPUs,
        },
        dispatch,
      );
      setIsBrokerStepTwo(true);
      return;
    }

    if (result && !result.success) {
      setErrorMessage(result.message || 'Launch failed');
      return;
    }

    // Auto-stage on a successful SRCNet launch only. Canfar staging is handled
    // inside launchCanfarSession().
    if (result?.success && props.stagingCatalog && sSiteValue !== '') {
      dispatch(
        autoStageForCatalog({
          catalog: props.stagingCatalog,
          toSiteUUID: sSiteValue,
        }),
      );
    }
  }; // handleLaunch

  const handleFormKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    const isButtonLike =
      target?.tagName === 'BUTTON' || target?.getAttribute('role') === 'button';
    const isTextArea = target instanceof HTMLTextAreaElement;

    if (event.defaultPrevented) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      props.onClose();
      return;
    }

    if (event.key === 'Enter' && !isButtonLike && !isTextArea) {
      event.preventDefault();
      handleLaunch();
    }
  }; // handleFormKeyDown

  //	------------------------------------------------------------
  //
  //	Handler for select box onChange event.
  //
  //	------------------------------------------------------------

  const onChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
    //
    const inputBox = event.target;

    // update the state after a select component change.
    if (inputBox.name === 'lst-node') {
      //
      setNodeValue(inputBox.value);

      // check if there is only one site for this node.
      let numSites: number = 0;
      let site: Site = UNDEFINED_SITE;
      for (let i: number = 0; i < sD_sites.length; i++) {
        if (sD_sites[i].node === inputBox.value) {
          site = sD_sites[i];
          numSites = numSites + 1;
        }
      }
      if (numSites !== 1) {
        site = UNDEFINED_SITE;
      }
      setSiteValue(site.site_uuid ?? '');
      //
    }
    if (inputBox.name === 'lst-site') {
      setSiteValue(inputBox.value);
    }
    //
  }; // onChangeHandler

  //	-------------------------------------------------
  //
  //	handle clicks for btn component.
  //
  //	-------------------------------------------------

  const onClickBtnHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    const button: HTMLButtonElement = event.currentTarget;

    // change the tab page.
    if (
      button.name === 'btn-srcnet' &&
      TAB === '' &&
      sG_userType === UserType.DEVELOPER
    ) {
      setPage('srcnet');
    }
    if (button.name === 'btn-canfar' && TAB === '') {
      setPage('canfar');
    }
    if (
      button.name === 'btn-broker' &&
      TAB === '' &&
      sG_userType === UserType.DEVELOPER
    ) {
      setPage('broker');
    }
    setErrorMessage('');

    //
  }; // onClickBtnHandler

  //	-------------------------------------------------
  //
  //	get the list of skaha resource options, such
  //	as ram and cores.
  //
  //	-------------------------------------------------

  async function getSkahaResourceOptions(args: { canfarService: CanfarServiceType }) {
    //
    const requestId = ++resourceOptionsRequestId.current;
    if (process.env.NODE_ENV === 'development') {
      console.log('[NewSessionPanel] fetching resource_options (cores/RAM)', {
        request_id: requestId,
        site_uuid: args.canfarService.site.site_uuid,
        canfar_id: args.canfarService.id,
      });
    }
    setLoadingOptions(true);
    try {
      //
      const response = await fetch(
        apiPrefix() +
          '/v1/skaha/resource_options?url=' +
          args.canfarService.url +
          '&site_uuid=' +
          args.canfarService.site.site_uuid,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        },
      );

      if (requestId !== resourceOptionsRequestId.current) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[NewSessionPanel] ignoring stale resource_options response', {
            request_id: requestId,
            current_request_id: resourceOptionsRequestId.current,
          });
        }
        return;
      }
      setLoadingOptions(false);

      if (response.status === 200) {
        //
        const options = await response.json();

        if (process.env.NODE_ENV === 'development') {
          console.log('[NewSessionPanel] resource_options received (cores/RAM only)', {
            request_id: requestId,
            has_cores: 'cores' in options,
            has_ram: 'ram' in options,
            has_images: 'images' in options,
          });
        }

        // get the list of cores.
        if ('cores' in options) {
          if ('default' in options.cores) {
            setCores(options.cores.default);
          }
          if ('options' in options.cores) {
            setCoreOptions(options.cores.options);
          }
        }

        // get the list of ram.
        if ('ram' in options) {
          if ('default' in options.ram) {
            setRam(options.ram.default);
          }
          if ('options' in options.ram) {
            setRamOptions(options.ram.options);
          }
        }
        //
      }
      //
    } catch (err) {
      if (requestId === resourceOptionsRequestId.current) {
        setLoadingOptions(false);
      }
    }
    //
  } // getSkahaResourceOptions

  //	------------------------------------------------------------
  //
  //	H O O K S
  //
  //	------------------------------------------------------------

  //	-------------------------------------------------
  //
  //	get the list of skaha resource options when the
  //	site changes, or on load when the site is
  //	pre-selected from props.
  //	(waits for CANFAR catalog if not loaded yet).
  //
  //	-------------------------------------------------

  useEffect(() => {
    //
    if (sCanfarService === undefined || sSiteValue === '') {
      if (process.env.NODE_ENV === 'development' && sSiteValue !== '') {
        console.log('[NewSessionPanel] no CANFAR service for site yet', {
          site_uuid: sSiteValue,
          catalog_size: sD_canfarServices.length,
        });
      }
      return;
    }
    // get the skaha resource options when the site is changed.
    getSkahaResourceOptions({ canfarService: sCanfarService });
    //
  }, [sCanfarService?.id, sSiteValue]);

  //	-------------------------------------------------
  //
  //	log the image dropdown state when the site, service
  //	type, or CANFAR catalog changes.
  //
  //	-------------------------------------------------

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      return;
    }
    if (sPage !== 'canfar' && !isBrokerStepTwo) {
      return;
    }

    const images = sCanfarService?.images?.filter(
      (image) => image.types.indexOf(sServiceType) > -1 || sServiceType === '',
    );
    console.log('[NewSessionPanel] image dropdown state', {
      site_uuid: sSiteValue,
      service_type: sServiceType,
      dropdown_count: images?.length ?? 0,
      image_ids: images?.map((image) => image.id) ?? [],
    });
  }, [sCanfarService, sSiteValue, sServiceType, sPage, isBrokerStepTwo]);

  //	-------------------------------------------------
  //
  //	when the selected compute service changes
  //	then select the corresponding service type.
  //
  //	-------------------------------------------------

  useEffect(() => {
    //
    if (sComputeService !== '') {
      //
      let selectedComputeService: ComputeServiceType | undefined =
        sD_computeServices.find((find) => find.compute_service_id === sComputeService);
      if (selectedComputeService !== undefined) {
        setServiceType(selectedComputeService.session_type);
      }
      //
    }
    //
  }, [sComputeService]);

  //	-------------------------------------------------
  //
  //	Hook that dynamically clears the selected compute service
  //
  //	-------------------------------------------------

  useEffect(() => {
    //
    // check if the selected compute service is valid for the current site and service type.
    let valid: boolean = false;
    if (sComputeService !== '') {
      //
      let selectedComputeService: ComputeServiceType | undefined =
        sD_computeServices.find((find) => find.compute_service_id === sComputeService);
      if (selectedComputeService !== undefined) {
        valid =
          (selectedComputeService.site.site_uuid === sSiteValue || sSiteValue === '') &&
          (selectedComputeService.session_type === sServiceType || sServiceType === '');
      }
      //
    } else {
      valid = true;
    }

    if (valid === false) {
      setComputeService(SERVICE_UUID);
    }

    // check if the selected service type exists on any compute service for this site.
    valid = false;
    if (sServiceType !== '' && sSiteValue !== '') {
      valid =
        getServiceTypes({ node: sNodeValue, site_uuid: sSiteValue }).findIndex(
          (find) => find === sServiceType,
        ) > -1;
    } else {
      valid = true;
    }

    if (valid === false) {
      setServiceType(SERVICE_TYPE);
    }
    //
  }, [sSiteValue, sServiceType]);

  //	-------------------------------------------------
  //
  //	When the service type changes, regenerate the session name using the fixed suffix.
  //
  //	-------------------------------------------------

  useEffect(() => {
    //
    if (sServiceType !== '') {
      setName(sServiceType + '-session-' + sessionSuffix.current);
    }
    //
  }, [sServiceType]);

  //	-------------------------------------------------
  //
  //	Handle the closing of the dialog.
  //
  //	-------------------------------------------------

  useEffect(() => {
    //
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        props.onClose();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
    //
  }, [props.onClose]);

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------
  let goBtnName: string =
    sPage === 'broker' && !isBrokerStepTwo ? t('Check availability') : t('Launch');
  let goBtnTooltip: string =
    sPage === 'broker' ? t('Get execution sites') : t('Launch a compute service');

  return (
    <div className="new-session-form" onKeyDownCapture={handleFormKeyDown}>
      {sErrorMessage && <div className="error-box">{sErrorMessage}</div>}

      {/* the srcnet and canfar tabs */}
      <div
        style={{ flex: '0 0 auto', width: '100%', display: 'flex', flexDirection: 'row' }}
      >
        {sG_userType === UserType.DEVELOPER && (
          <ToolSmall
            name="btn-srcnet"
            text="SRCNet"
            onClick={onClickBtnHandler}
            selected={sPage === 'srcnet'}
            disabled={TAB !== '' && TAB !== 'srcnet'}
            left={true}
            right={false}
            tabWidth={232}
          />
        )}
        <ToolSmall
          name="btn-canfar"
          text="Canfar"
          onClick={onClickBtnHandler}
          selected={sPage === 'canfar'}
          disabled={TAB !== '' && TAB !== 'canfar'}
          left={sG_userType !== UserType.DEVELOPER}
          right={sG_userType !== UserType.DEVELOPER}
          tabWidth={232}
        />
        {sG_userType === UserType.DEVELOPER && (
          <ToolSmall
            name="btn-broker"
            text="Execution broker"
            onClick={onClickBtnHandler}
            selected={sPage === 'broker'}
            disabled={TAB !== '' && TAB !== 'broker'}
            left={false}
            right={true}
            tabWidth={232}
          />
        )}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <div
          style={{
            flex: '1 1',
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
          }}
        >
          <label
            id="new-session-service-type-label"
            className="text-colour"
            style={{
              margin: '5px 0px 0px 0px',
            }}
          >
            Service type:
          </label>
          <select
            aria-labelledby="new-session-session-service-type-label"
            name="lst-service-types"
            className="listbox"
            multiple={false}
            value={sServiceType}
            onChange={(e) => {
              if (sPage === 'broker' && isBrokerStepTwo) {
                setIsBrokerStepTwo(false);
                //setPage('broker');
              }
              setServiceType(e.target.value);
            }}
            data-placeholder-shown={
              sServiceType === '' ||
              getServiceTypes({ node: sNodeValue, site_uuid: sSiteValue }).indexOf(
                sServiceType,
              ) === -1
                ? 'T'
                : 'F'
            }
            style={{ lineHeight: '100%', borderRadius: '0px' }}
            disabled={SERVICE_TYPE !== ''}
            data-disabled={SERVICE_TYPE !== '' ? 'T' : 'F'}
          >
            <option hidden label={t('Select service type')} value="" />
            {getServiceTypes({ node: sNodeValue, site_uuid: sSiteValue }).map(
              (service_type) => (
                <option
                  key={service_type}
                  label={service_type.toUpperCase()}
                  value={service_type}
                />
              ),
            )}
          </select>

          <div
            style={{
              flex: '0 0 auto',
              width: '100%',
              gap: '5px',
              display:
                sPage !== 'broker' || (sPage === 'broker' && isBrokerStepTwo)
                  ? 'flex'
                  : 'none',
              flexDirection: 'column',
            }}
          >
            {/* node filter box */}
            <label
              id="new-session-node-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              Node:
            </label>
            <select
              aria-labelledby="new-session-node-label"
              name="lst-node"
              className="listbox"
              multiple={false}
              value={sNodeValue}
              onChange={onChangeHandler}
              data-placeholder-shown={sNodeValue === '' ? 'T' : 'F'}
              style={{ lineHeight: '100%', borderRadius: '0px' }}
              disabled={NODE !== ''}
              data-disabled={NODE !== '' ? 'T' : 'F'}
            >
              <option hidden label={t('Select node')} value="" />
              {getNodes({
                sites:
                  sPage === 'broker' && sBrokerSites.length > 0 ? sBrokerSites : sD_sites,
              }).map((node) => (
                <option key={node ?? ''} label={node ?? ''} value={node ?? ''} />
              ))}
            </select>

            {/* site filter box */}
            <label
              id="new-session-site-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              Site:
            </label>
            <select
              aria-labelledby="new-session-site-label"
              name="lst-site"
              className="listbox"
              multiple={false}
              value={sSiteValue}
              onChange={onChangeHandler}
              data-placeholder-shown={sSiteValue === '' ? 'T' : 'F'}
              style={{ lineHeight: '100%', borderRadius: '0px' }}
              disabled={SITE_UUID !== ''}
              data-disabled={SITE_UUID !== '' ? 'T' : 'F'}
            >
              <option hidden label={t('Select site')} value="" />
              {getSites({ node: sNodeValue }).map((item, index) => (
                <option key={index} label={item.site ?? ''} value={item.site_uuid} />
              ))}
            </select>
          </div>
          <div
            style={{
              flex: '0 0 auto',
              width: '100%',
              gap: '5px',
              display: sPage === 'broker' && !isBrokerStepTwo ? 'flex' : 'none',
              flexDirection: 'column',
            }}
          >
            <div className="checkbox-container">
              <input
                aria-labelledby="new-session-session-name-label"
                id="require-gpus"
                name="require-gpus"
                className="checkbox"
                type="checkbox"
                checked={sRequireGPUs}
                onChange={(e) => setRequireGPUs(e.target.checked)}
                title="Require GPUs"
              />
              <label htmlFor="require-gpus">{t('Require GPUs')}</label>
            </div>
          </div>

          <div
            style={{
              flex: '0 0 auto',
              width: '100%',
              gap: '5px',
              display:
                sPage === 'canfar' || (sPage === 'broker' && isBrokerStepTwo)
                  ? 'flex'
                  : 'none',
              flexDirection: 'column',
            }}
          >
            <label
              id="new-session-images-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              Select image:
            </label>
            <select
              aria-labelledby="new-session-images-label"
              name="lst-images"
              className="listbox"
              multiple={false}
              value={sImage}
              onChange={(e) => setImage(e.target.value)}
              data-placeholder-shown={sImage === '' ? 'T' : 'F'}
              style={{ borderRadius: '0px' }}
              disabled={sPage !== 'broker' && (sNodeValue === '' || sSiteValue === '')}
              data-disabled={
                sPage !== 'broker' && (sNodeValue === '' || sSiteValue === '') ? 'T' : 'F'
              }
            >
              <option hidden label={t('Select image')} value="" />
              {getImages()?.map((image, index) => (
                <option key={index} label={image.id} value={image.id} />
              ))}
            </select>

            <label
              id="new-session-session-name-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              Session name:
            </label>
            <input
              aria-labelledby="new-session-session-name-label"
              className="input-field"
              type="text"
              name="name"
              onChange={(e) => setName(e.target.value)}
              onKeyPress={alphanumericKeysOnly}
              maxLength={255}
              value={sName}
              placeholder="Session name"
              title="Session name"
            />
          </div>
        </div>

        <div style={{ flex: '0 0 15px' }} />
        <div
          style={{
            flex: '1 1',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              flex: '0 0 auto',
              width: '100%',
              gap: '5px',
              display: sPage === 'srcnet' ? 'flex' : 'none',
              flexDirection: 'column',
            }}
          >
            <label
              id="new-session-duration-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              Duration:
            </label>
            <select
              aria-labelledby="new-session-duration-label"
              name="lst-duration-type"
              className="listbox"
              multiple={false}
              value={sDuration}
              onChange={(e) => setDuration(e.target.value)}
              style={{ borderRadius: '0px' }}
            >
              {sessionDurationOptions.map(({ label, value }) => (
                <option key={value} label={label} value={value} />
              ))}
            </select>
            <label
              id="new-session-available-services-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              Available services:
            </label>
            <select
              aria-labelledby="new-session-available-services-label"
              name="lst-compute-service"
              className="listbox"
              multiple={false}
              value={sComputeService}
              onChange={(e) => setComputeService(e.target.value)}
              data-placeholder-shown={sComputeService === '' ? 'T' : 'F'}
              style={{ lineHeight: '100%', borderRadius: '0px' }}
              disabled={SERVICE_UUID !== '' || sNodeValue === '' || sSiteValue === ''}
              data-disabled={
                SERVICE_UUID !== '' || sNodeValue === '' || sSiteValue === '' ? 'T' : 'F'
              }
            >
              <option hidden label={t('Select service')} value="" />
              {sD_computeServices
                .filter(
                  (filter) =>
                    filter.site.node === sNodeValue &&
                    (filter.site.site_uuid === sSiteValue || sSiteValue === '') &&
                    (filter.session_type === sServiceType ||
                      (sServiceType === '' &&
                        ['jupyterhub', 'carta'].indexOf(filter.session_type) > -1)),
                )
                .map((service) => (
                  <option
                    key={service.compute_service_id}
                    label={
                      service.service_name
                        ? service.service_name
                        : `${service.session_type.toUpperCase()} (no description)`
                    }
                    value={service.compute_service_id}
                  />
                ))}
            </select>
          </div>

          <div
            style={{
              flex: '0 0 auto',
              width: '100%',
              gap: '5px',
              display:
                sPage === 'canfar' || (sPage === 'broker' && isBrokerStepTwo)
                  ? 'flex'
                  : 'none',
              flexDirection: 'column',
            }}
          >
            <label
              id="new-session-number-cores-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              Number of cores:
            </label>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <PleaseWait text="" visible={sLoadingOptions} small={true} />
              <div
                style={{ flex: '0 0 6px', display: sLoadingOptions ? 'flex' : 'none' }}
              />
              <select
                aria-labelledby="new-session-number-cores-label"
                name="lst-cores"
                className="listbox"
                multiple={false}
                value={sCores}
                onChange={(e) => setCores(e.target.value)}
                data-placeholder-shown={sCores === '' ? 'T' : 'F'}
                style={{ flex: '1 1', borderRadius: '0px' }}
                disabled={sNodeValue === '' || sSiteValue === ''}
                data-disabled={sNodeValue === '' || sSiteValue === '' ? 'T' : 'F'}
              >
                <option hidden label={t('Select number of cores')} value="" />
                {sCoreOptions.map((value, index) => (
                  <option key={index} label={value + ' core(s)'} value={value} />
                ))}
              </select>
            </div>
            <label
              id="new-session-ram-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              RAM:
            </label>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <PleaseWait text="" visible={sLoadingOptions} small={true} />
              <div
                style={{ flex: '0 0 6px', display: sLoadingOptions ? 'flex' : 'none' }}
              />
              <select
                aria-labelledby="new-session-ram-label"
                name="lst-ram"
                className="listbox"
                multiple={false}
                value={sRam}
                onChange={(e) => setRam(e.target.value)}
                data-placeholder-shown={sRam === '' ? 'T' : 'F'}
                style={{ flex: '1 1', borderRadius: '0px' }}
                disabled={sNodeValue === '' || sSiteValue === ''}
                data-disabled={sNodeValue === '' || sSiteValue === '' ? 'T' : 'F'}
              >
                <option hidden label={t('Select RAM')} value="" />
                {sRamOptions.map((value, index) => (
                  <option key={index} label={value + ' GB'} value={value} />
                ))}
              </select>
            </div>
            <label
              id="new-session-cmd-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              cmd (headless sessions only):
            </label>
            <input
              aria-labelledby="new-session-cmd-label"
              className="input-field"
              type="text"
              name="cmd"
              onChange={(e) => setCmd(e.target.value)}
              maxLength={255}
              value={sCmd}
              placeholder="Enter cmd"
              title="cmd (headless sessions only)"
              disabled={sServiceType !== 'headless'}
              data-disabled={sServiceType !== 'headless' ? 'T' : 'F'}
            />
            <label
              id="new-session-args-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              args (headless sessions only):
            </label>
            <input
              aria-labelledby="new-session-args-label"
              className="input-field"
              type="text"
              name="args"
              onChange={(e) => setArgs(e.target.value)}
              maxLength={255}
              value={sArgs}
              placeholder="Enter args"
              title="args (headless sessions only)"
              disabled={sServiceType !== 'headless'}
              data-disabled={sServiceType !== 'headless' ? 'T' : 'F'}
            />
            <label
              id="new-session-env-label"
              className="text-colour"
              style={{
                margin: '5px 0px 0px 0px',
              }}
            >
              env (headless sessions only):
            </label>
            <input
              aria-labelledby="new-session-env-label"
              className="input-field"
              type="text"
              name="env"
              onChange={(e) => setEnv(e.target.value)}
              maxLength={255}
              value={sEnv}
              placeholder="Enter env"
              title="env (headless sessions only)"
              disabled={sServiceType !== 'headless'}
              data-disabled={sServiceType !== 'headless' ? 'T' : 'F'}
            />
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          margin: '15px 0px 0px 0px',
        }}
      >
        <div style={{ flex: '0 0 auto' }}>
          <ToolButton
            key={t('Cancel')}
            name="cancel"
            onClick={props.onClose}
            tooltip="Cancel launch compute session modal"
            text={t('Cancel')}
            type={ToolButtonType.SECONDARY}
          />
        </div>
        <div style={{ padding: '5px' }} />
        <div style={{ flex: '0 0 auto' }}>
          <ToolButton
            key={goBtnName}
            name="launch"
            onClick={handleLaunch}
            tooltip={goBtnTooltip}
            text={goBtnName}
            type={ToolButtonType.PRIMARY}
          />
        </div>
      </div>
    </div>
  );
};

export default NewSessionPanel;
